import { CalendarPlus } from "lucide-react";
import { useTranslate } from "ra-core";

import { useAjustes } from "../datos/useAjustes";
import { enlaceGoogleCalendar } from "../dominio/calendario";
import type { Negocio } from "../dominio/tipos";

/**
 * «Añadir a Google Calendar» (Robert, 14/10/2026): el siguiente paso, la consultoría y la
 * presentación guardados en la ficha, cada uno con su enlace que abre Google Calendar con la cita
 * rellenada (título, fecha u hora, persona, teléfono y enlace a la ficha). Sin conexión ni permisos.
 */
export const CitasGoogleCalendar = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  // 41: con el calendario propio, la consultoría ya va sola al calendario de info@.
  const { ajustes } = useAjustes();
  const ficha = `${window.location.origin}/#/negocios?ficha=${negocio.negocio_id}`;
  const detalles = [negocio.persona_contacto, negocio.telefono, ficha]
    .map((x) => (x ?? "").trim())
    .filter(Boolean)
    .join("\n");
  const citas = [
    {
      clave: "siguiente",
      etiqueta: "nevora.google_calendar.siguiente",
      enlace: enlaceGoogleCalendar({
        titulo: `${negocio.siguiente_paso?.trim() || translate("nevora.agenda.siguiente_paso")} · ${negocio.nombre}`,
        // 33: con hora, cita de media hora a esa hora; sin hora, de día entero.
        ...(negocio.hora_siguiente_paso && negocio.fecha_siguiente_paso
          ? {
              inicio: new Date(
                `${negocio.fecha_siguiente_paso}T${negocio.hora_siguiente_paso.slice(0, 5)}`,
              ).toISOString(),
            }
          : { dia: negocio.fecha_siguiente_paso }),
        detalles,
      }),
    },
    {
      clave: "consultoria",
      etiqueta: "nevora.google_calendar.consultoria",
      enlace:
        negocio.etapa === "consultoria_agendada" && !ajustes.agenda.propia
          ? enlaceGoogleCalendar({
              titulo: `${translate("nevora.agenda.reunion")} · ${negocio.nombre}`,
              inicio: negocio.fecha_reunion,
              detalles,
            })
          : null,
    },
    {
      clave: "presentacion",
      etiqueta: "nevora.google_calendar.presentacion",
      enlace:
        negocio.etapa === "presentacion_agendada"
          ? enlaceGoogleCalendar({
              titulo: `${translate("nevora.agenda.presentacion")} · ${negocio.nombre}`,
              inicio: negocio.fecha_presentacion,
              detalles,
            })
          : null,
    },
  ].filter(
    (c): c is { clave: string; etiqueta: string; enlace: string } => !!c.enlace,
  );
  if (!citas.length) return null;
  return (
    <div className="md:col-span-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
      {citas.map((c) => (
        <a
          key={c.clave}
          href={c.enlace}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 underline underline-offset-2 text-muted-foreground hover:text-foreground"
          data-testid={`google-calendar-${c.clave}`}
        >
          <CalendarPlus className="size-4" aria-hidden />
          {translate(c.etiqueta)}
        </a>
      ))}
    </div>
  );
};
